// Framework-free GA4 wrapper (PACKAGING_PLAN.md §3.5). No Vue, no store: the app
// gives it the measurement id and a `getContext()` getter for the parameters it wants
// on every hit, and keeps the id declared in exactly one place (its config/constants).
//
// It also owns the tag load: the `<script async src="https://www.googletagmanager.com/gtag/js?id=…">`
// pair that used to sit in index.html is created here on first use, so an app cannot
// load the tag with one id and configure it with another.

/**
 * @typedef {object} Analytics
 * @property {() => void} init  Sends the initial `config` once (idempotent).
 * @property {(eventName: string, params?: object, userEmail?: string|null) => void} trackEvent
 * @property {(pageUrl: string, userEmail?: string|null) => void} trackPage
 */

/**
 * @param {object} options
 * @param {string} options.measurementId   GA4 measurement id (`G-…`). Required.
 * @param {() => object} [options.getContext]  Parameters merged into every hit, read at
 *   send time (e.g. `() => ({ parent_url: store.state.settings.apiUrl })`).
 * @param {boolean} [options.load=true]  Inject the gtag.js `<script>` on first use. Pass
 *   `false` when the page already loads it.
 * @returns {Analytics}
 */
export function createAnalytics({ measurementId, getContext = () => ({}), load = true } = {}) {
  if (!measurementId) throw new Error('@rdrive/helper-analytics: createAnalytics({ measurementId }) is required.');
  let configured = false;

  const ensureGtag = () => {
    if (typeof window === 'undefined') return false;
    if (!window.gtag) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      if (load) {
        const s = document.createElement('script');
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
        document.head.appendChild(s);
      }
    }
    return true;
  };

  return {
    init() {
      if (configured || !ensureGtag()) return;
      window.gtag('config', measurementId, { custom_map: { dimension1: 'parent_url' }, ...getContext() });
      configured = true;
    },
    trackEvent(eventName, params = {}, userEmail = null) {
      if (!ensureGtag()) return;
      window.gtag('event', eventName, {
        ...getContext(),
        ...params,
        ...(userEmail ? { user_email: userEmail } : {}),
      });
    },
    trackPage(pageUrl, userEmail = null) {
      if (!ensureGtag()) return;
      window.gtag('config', measurementId, {
        ...getContext(),
        page_path: pageUrl,
        ...(userEmail ? { user_email: userEmail } : {}),
      });
    },
  };
}
